import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from "react";
import { Pagination, Select, message } from "antd";
import type { TablePaginationConfig } from "antd";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { DownloadOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import CustomDatePicker from "../../CommonComponents/CustomDatePicker/CustomDatePicker";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import DataTableBase from "../../shared/dashboards/DataTable/DataTable";
import SharedSelect from "../../shared/Select/Select";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import AppContext from "../../../context/AppContext";
import { KPI_CARD_BACKGROUND, SIDER_TABS } from "../../../utils/Constants";
import { getEffectiveAdminTimezone } from "../../../utils/dateService";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useCasinoFinanceReport } from "../../../queries/casino/useCasinoFinanceReport";
import { useCasinoFinanceKpis } from "../../../queries/casino/useCasinoFinanceKpis";
import { normalizeAntSelectNumberValue } from "../../ReportingSummary/reportingSummaryUtils";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { PAGE_SIZE_OPTIONS } from "../../ReportingSummary/reportingSummaryConstants";
import { parseCasinoKpiFilterMetadata } from "../../../queries/casino-kpis/casinoKpiFilters";
import {
  type CasinoFinanceRow,
  type CasinoFinanceSortKey,
  getCasinoFinanceColumns,
} from "./casinoFinanceColumns";
import { buildCasinoFinanceTableCsv } from "./casinoFinanceCsv";
import { sortCasinoFinanceRows } from "./casinoFinanceSort";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import "./stylesCasinoFinanceReports.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const DataTable = DataTableBase as ComponentType<Record<string, unknown>>;

const DEFAULT_PAGE_SIZE = 20;

const ALL_KEY = "all";

type KpiFilterItem = {
  key: string;
  multiSelect: boolean;
  options: string[];
  defaultValues: string[];
  selectedValues: string[];
};

function getDefaultFinanceDateRange(): [Dayjs, Dayjs] {
  const tz = getEffectiveAdminTimezone();
  return [
    dayjs().tz(tz).startOf("month"),
    dayjs().tz(tz).endOf("month"),
  ];
}

function CasinoFinanceReports() {
  usePageTitle(AppStrings.CASINO_FINANCE_REPORTS_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);

  const [dates, setDates] = useState<[Dayjs, Dayjs]>(getDefaultFinanceDateRange);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortState, setSortState] = useState<{
    field: CasinoFinanceSortKey | null;
    order: "ascend" | "descend" | null;
  }>({ field: null, order: null });

  /**
   * Single filter dropdown: label stays `AppStrings.PROVIDER` (unchanged copy).
   * Options and API param are strictly `meta.filters[].key === "subprovider"`.
   */
  const [draftSubprovider, setDraftSubprovider] = useState(ALL_KEY);
  const [appliedSubprovider, setAppliedSubprovider] = useState(ALL_KEY);
  const [draftGame, setDraftGame] = useState(ALL_KEY);
  const [appliedGame, setAppliedGame] = useState(ALL_KEY);
  const [draftMoneyTypes, setDraftMoneyTypes] = useState<string[]>([]);
  const [appliedMoneyTypes, setAppliedMoneyTypes] = useState<string[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filtersSyncRef = useRef(false);

  const analyticsQueryParams = useMemo(
    () => ({
      start_date: dates[0]?.format("YYYY-MM-DD"),
      end_date: dates[1]?.format("YYYY-MM-DD"),
      limit: pageSize,
      page: currentPage,
      game: appliedGame,
      subprovider: appliedSubprovider,
      money_type: appliedMoneyTypes,
    }),
    [
      dates,
      pageSize,
      currentPage,
      appliedGame,
      appliedSubprovider,
      appliedMoneyTypes,
    ],
  );

  const liveQueryEnabled = Boolean(
    analyticsQueryParams.start_date && analyticsQueryParams.end_date,
  );

  const draftSubForApi =
    draftSubprovider === ALL_KEY ? undefined : draftSubprovider;
  const appliedSubForApi =
    appliedSubprovider === ALL_KEY ? undefined : appliedSubprovider;
  const appliedGameForApi = appliedGame === ALL_KEY ? undefined : appliedGame;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isPlaceholderData,
  } = useCasinoFinanceReport(analyticsQueryParams, {
    enabled: liveQueryEnabled,
  });
  const {
    data: kpiMetaPayload,
    isLoading: isKpiMetaLoading,
    isFetching: isKpiMetaFetching,
    isPlaceholderData: isKpiMetaPlaceholderData,
  } = useCasinoFinanceKpis(
    {
      start_date: analyticsQueryParams.start_date,
      end_date: analyticsQueryParams.end_date,
      subprovider: draftSubForApi,
      game: undefined,
    },
    { enabled: liveQueryEnabled },
  );
  const {
    data: kpiSummaryPayload,
    isLoading: isKpiSummaryLoading,
    isFetching: isKpiSummaryFetching,
    isPlaceholderData: isKpiSummaryPlaceholderData,
  } = useCasinoFinanceKpis(
    {
      start_date: analyticsQueryParams.start_date,
      end_date: analyticsQueryParams.end_date,
      subprovider: appliedSubForApi,
      game: appliedGameForApi,
      money_type: appliedMoneyTypes,
    },
    { enabled: liveQueryEnabled },
  );

  const baseRows = useMemo(
    () => (data?.rows ?? []) as CasinoFinanceRow[],
    [data?.rows],
  );

  const sortedRows = useMemo(
    () =>
      sortCasinoFinanceRows(baseRows, sortState.field, sortState.order),
    [baseRows, sortState.field, sortState.order],
  );

  const displayRows = sortedRows;

  const displayTotal = data?.total ?? sortedRows.length;

  const kpiFilters = useMemo((): KpiFilterItem[] => {
    const raw = kpiMetaPayload?.filters;
    if (!Array.isArray(raw)) return [];
    return raw.filter(Boolean) as KpiFilterItem[];
  }, [kpiMetaPayload?.filters]);

  const kpiFilterSubprovider = kpiFilters.find((f) => f.key === "subprovider");
  const kpiFilterGame = kpiFilters.find((f) => f.key === "game");
  const currencyTypeOptions = useMemo(
    () => parseCasinoKpiFilterMetadata(kpiMetaPayload).options,
    [kpiMetaPayload],
  );

  const displaySummary = kpiSummaryPayload?.summary ?? data?.summary ?? null;

  // `keepPreviousData` keeps prior rows while a new range loads — `isLoading` stays false, so use
  // `isFetching` + `isPlaceholderData` to show the loader after date (or page) changes.
  const showTableLoader =
    isLoading || (isFetching && Boolean(isPlaceholderData));
  const showKpiLoader =
    isKpiSummaryLoading ||
    (isKpiSummaryFetching && Boolean(isKpiSummaryPlaceholderData));
  const filterControlsLoading =
    isKpiMetaLoading ||
    (isKpiMetaFetching && Boolean(isKpiMetaPlaceholderData));
  const showTableError = isError;

  const subproviderSelectOptions = useMemo(() => {
    const opts = kpiFilterSubprovider?.options?.length
      ? kpiFilterSubprovider.options
      : [ALL_KEY];
    return opts.map((val) => ({
      value: val,
      label: val === ALL_KEY ? AppStrings.ALL : val,
    }));
  }, [kpiFilterSubprovider]);

  const gameSelectOptions = useMemo(() => {
    const opts = kpiFilterGame?.options?.length ? kpiFilterGame.options : [ALL_KEY];
    return opts.map((val) => ({
      value: val,
      label: val === ALL_KEY ? AppStrings.ALL : val,
    }));
  }, [kpiFilterGame]);

  const filtersDirty =
    draftSubprovider !== appliedSubprovider ||
    draftGame !== appliedGame ||
    JSON.stringify(draftMoneyTypes) !== JSON.stringify(appliedMoneyTypes);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "FINANCE");
  }, [setActiveSidebarTab]);

  // After a new date range, re-apply game/provider defaults from the next KPIs payload.
  useEffect(() => {
    filtersSyncRef.current = false;
  }, [analyticsQueryParams.start_date, analyticsQueryParams.end_date]);

  useEffect(() => {
    if (filtersSyncRef.current) return;
    if (kpiMetaPayload == null) return;
    // `keepPreviousData` can surface prior-range KPIs briefly after a date change; skip until fresh.
    if (isKpiMetaPlaceholderData) return;
    const list = kpiFilters;
    if (!list?.length) {
      filtersSyncRef.current = true;
      return;
    }
    const gameF = list.find((f) => f.key === "game");
    const subF = list.find((f) => f.key === "subprovider");
    if (gameF) {
      const g = gameF.selectedValues[0] ?? gameF.defaultValues[0] ?? ALL_KEY;
      setDraftGame(g);
      setAppliedGame(g);
    }
    if (subF) {
      const p = subF.selectedValues[0] ?? subF.defaultValues[0] ?? ALL_KEY;
      setDraftSubprovider(p);
      setAppliedSubprovider(p);
    }
    filtersSyncRef.current = true;
  }, [kpiMetaPayload, isKpiMetaPlaceholderData, kpiFilters]);

  const currencySymbol = useCurrencySymbol();
  const columns = useMemo(
    () => getCasinoFinanceColumns(currencySymbol),
    [currencySymbol],
  );

  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<CasinoFinanceRow>
        | SorterResult<CasinoFinanceRow>[],
    ) => {
      const one = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = one?.field as CasinoFinanceSortKey | undefined;
      const order = one?.order ?? null;
      setSortState({
        field: field ?? null,
        order,
      });
      setCurrentPage(1);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedSubprovider(draftSubprovider);
    setAppliedGame(draftGame);
    setAppliedMoneyTypes(draftMoneyTypes);
    setCurrentPage(1);
  }, [draftGame, draftMoneyTypes, draftSubprovider]);

  const handleClearFilters = useCallback(() => {
    const list = kpiFilters;
    if (list?.length) {
      const gameF = list.find((f) => f.key === "game");
      const subF = list.find((f) => f.key === "subprovider");
      if (gameF) {
        const g = gameF.defaultValues[0] ?? ALL_KEY;
        setDraftGame(g);
        setAppliedGame(g);
      } else {
        setDraftGame(ALL_KEY);
        setAppliedGame(ALL_KEY);
      }
      if (subF) {
        const p = subF.defaultValues[0] ?? ALL_KEY;
        setDraftSubprovider(p);
        setAppliedSubprovider(p);
      } else {
        setDraftSubprovider(ALL_KEY);
        setAppliedSubprovider(ALL_KEY);
      }
    } else {
      setDraftSubprovider(ALL_KEY);
      setAppliedSubprovider(ALL_KEY);
      setDraftGame(ALL_KEY);
      setAppliedGame(ALL_KEY);
    }
    setDraftMoneyTypes([]);
    setAppliedMoneyTypes([]);
    setCurrentPage(1);
  }, [kpiFilters]);

  const handleDateRangeQuery = useCallback(() => {
    setCurrentPage(1);
    refetch();
  }, [refetch]);

  const handleExport = useCallback(() => {
    if (displayRows.length === 0) {
      message.warning(AppStrings.CASINO_FINANCE_EXPORT_PENDING);
      return;
    }
    try {
      const csv = buildCasinoFinanceTableCsv(displayRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "casino-finance-report.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console -- surfaced to operators; user sees message.error
      console.error("[CasinoFinanceExport]", err);
      message.error(AppStrings.CASINO_FINANCE_EXPORT_ERROR);
    }
  }, [displayRows]);

  return (
    <>
      <div className="casino-finance-page">
        <div className="casino-finance-header">
          <div className="casino-finance-header-controls manual-actions-header-controls">
            <button
              type="button"
              className="casino-finance-filter-toggle"
              aria-expanded={filtersExpanded}
              aria-label={AppStrings.PAM_REPORT_FILTERS}
              onClick={() => setFiltersExpanded((expanded) => !expanded)}
            >
              <Filter size={18} aria-hidden />
              {filtersExpanded ? (
                <ChevronUp size={18} aria-hidden />
              ) : (
                <ChevronDown size={18} aria-hidden />
              )}
            </button>
            <CustomDatePicker
              dates={dates}
              setDates={setDates}
              getData={handleDateRangeQuery}
              showPresets={true}
              startRange={AppStrings.THIS_MONTH}
            />
            <button
              type="button"
              className="download-button"
              aria-label="Download"
              onClick={handleExport}
            >
              <DownloadOutlined />
            </button>
          </div>
        </div>

        {filtersExpanded && (
        <div className="casino-finance-filters">
          <div className="casino-finance-filter-field">
            <span className="casino-finance-filter-label">
              {AppStrings.CASINO_KPI_CURRENCY_TYPE}
            </span>
            <SharedSelect<string[]>
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder={AppStrings.CASINO_KPI_ALL_CURRENCY_TYPES}
              loading={filterControlsLoading}
              className="casino-finance-filter-select"
              aria-label={AppStrings.CASINO_KPI_CURRENCY_TYPE}
              options={currencyTypeOptions}
              value={draftMoneyTypes}
              onChange={(values) =>
                setDraftMoneyTypes(values.map((value) => String(value)))
              }
              maxTagCount="responsive"
            />
          </div>
          <div className="casino-finance-filter-field">
            <span className="casino-finance-filter-label">
              {AppStrings.PROVIDER}
            </span>
            <Select
              showSearch
              placeholder={AppStrings.NOT_SELECTED}
              loading={filterControlsLoading}
              className="casino-finance-filter-select"
              aria-label={AppStrings.PROVIDER}
              options={subproviderSelectOptions}
              value={draftSubprovider}
              onChange={(v) => {
                const next = String(v);
                setDraftSubprovider(next);
                setDraftGame(ALL_KEY);
              }}
              optionFilterProp="label"
            />
          </div>
          <div className="casino-finance-filter-field">
            <span className="casino-finance-filter-label">{AppStrings.GAME}</span>
            <Select
              showSearch
              placeholder={AppStrings.NOT_SELECTED}
              loading={filterControlsLoading}
              className="casino-finance-filter-select"
              aria-label={AppStrings.GAME}
              options={gameSelectOptions}
              value={draftGame}
              onChange={(v) => setDraftGame(String(v))}
              optionFilterProp="label"
            />
          </div>
          <div className="staff-selector-action-buttons casino-finance-filter-actions">
            <button
              type="button"
              className="clear"
              onClick={handleClearFilters}
            >
              {AppStrings.CLEAR}
            </button>
            <button
              type="button"
              className={`apply ${filtersDirty ? "apply--active" : ""}`}
              onClick={handleApplyFilters}
              disabled={!filtersDirty}
            >
              {AppStrings.APPLY}
            </button>
          </div>
        </div>
        )}

        <div className="casino-finance-kpi-row">
          <KpiCard
            title={AppStrings.CASINO_FINANCE_KPI_ROUNDS}
            value={displaySummary?.rounds ?? 0}
            hugeValue={false}
            percentage={null}
            isApiRunning={showKpiLoader}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
            maximumFractionDigits={0}
          />
          <KpiCard
            title={AppStrings.CASINO_FINANCE_KPI_PLAYERS}
            value={displaySummary?.players ?? 0}
            hugeValue={false}
            percentage={null}
            isApiRunning={showKpiLoader}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
            maximumFractionDigits={0}
          />
          <KpiCard
            title={AppStrings.CASINO_FINANCE_KPI_TURNOVER}
            value={displaySummary?.turnover ?? 0}
            hugeValue={false}
            percentage={null}
            isApiRunning={showKpiLoader}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
            currencySymbol={currencySymbol}
            maximumFractionDigits={2}
          />
          <KpiCard
            title={AppStrings.CASINO_FINANCE_KPI_WINS}
            value={displaySummary?.wins ?? 0}
            hugeValue={false}
            percentage={null}
            isApiRunning={showKpiLoader}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
            currencySymbol={currencySymbol}
            maximumFractionDigits={2}
          />
          <KpiCard
            title={AppStrings.GGR}
            value={displaySummary?.ggr ?? 0}
            hugeValue={false}
            percentage={null}
            isApiRunning={showKpiLoader}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
            currencySymbol={currencySymbol}
            maximumFractionDigits={2}
          />
        </div>

        <div className="casino-finance-table-section">
          {showTableError ? (
            <div className="casino-finance-error">
              <p>{AppStrings.CASINO_FINANCE_LOAD_ERROR}</p>
              <button type="button" className="casino-finance-retry" onClick={() => refetch()}>
                {AppStrings.RETRY}
              </button>
              {error?.message ? (
                <pre className="casino-finance-error-detail">{error.message}</pre>
              ) : null}
            </div>
          ) : showTableLoader ? (
            <div className="casino-finance-loader">
              <RebetLoader height="100px" width="100px" />
            </div>
          ) : (
            <>
              {/* Column drag-to-reorder is a separate follow-up; table uses sort on header only. */}
              <DataTable
                id="casino-finance-table"
                rowKey="key"
                className="casino-finance-table"
                columns={columns}
                dataSource={displayRows}
                pagination={false}
                onChange={handleTableChange}
                showSorterTooltip={{ target: "sorter-icon" }}
                rowClassName={(_record: CasinoFinanceRow, index: number) =>
                  index % 2 === 1 ? "casino-finance-table-row--alt" : ""
                }
                scrollPreset="verticalOnly"
              />
              <div className="casino-finance-pagination-bar">
                <div className="casino-finance-pagination-left">
                  <Select
                    value={pageSize}
                    onChange={(v) => {
                      setPageSize(normalizeAntSelectNumberValue(v));
                      setCurrentPage(1);
                    }}
                    className="casino-finance-page-size-select"
                    options={PAGE_SIZE_OPTIONS.map((n) => ({
                      value: n,
                      label: String(n),
                    }))}
                  />
                  <span className="casino-finance-per-page-label">
                    {AppStrings.REPORTING_PAGINATION_PER_PAGE}{" "}
                    {AppStrings.CASINO_FINANCE_PAGINATION_TOTAL_LABEL.replace(
                      "{total}",
                      String(displayTotal),
                    )}
                  </span>
                </div>
                <div className="casino-finance-pagination-right">
                  <Pagination
                    current={currentPage}
                    total={displayTotal}
                    pageSize={pageSize}
                    showSizeChanger={false}
                    onChange={(p) => setCurrentPage(p)}
                    itemRender={(_pageNum, type, originalElement) => {
                      if (type === "prev") {
                        return (
                          <span className="casino-finance-pagination-nav">
                            {originalElement}
                            <span>{AppStrings.BACK}</span>
                          </span>
                        );
                      }
                      if (type === "next") {
                        return (
                          <span className="casino-finance-pagination-nav">
                            <span>{AppStrings.NEXT}</span>
                            {originalElement}
                          </span>
                        );
                      }
                      return originalElement;
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default CasinoFinanceReports;
